import React, { useState } from "react";
import {
  confirmAuthenticatorSetup,
  switchMfaLoginMethod,
  buildQrCodeUrl,
  MFA_METHOD_AUTHENTICATOR,
} from "../../services/mfaService";
import "./MfaLoginAuthenticatorSetup.scss";

function MfaLoginAuthenticatorSetup({ challenge, onComplete, onBack, loading }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const staffId = challenge?.StaffId ?? challenge?.staffId;
  const token = challenge?.MfaSessionToken ?? challenge?.mfaSessionToken;
  const otpAuthUri = challenge?.OtpAuthUri ?? challenge?.otpAuthUri ?? "";
  const secretKey = challenge?.SecretKey ?? challenge?.secretKey ?? "";

  const handleDigitChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
  };

  const handleConfirm = async () => {
    const code = digits.join("");
    if (code.length !== 6 || !staffId || !token) return;

    setSubmitting(true);
    setError("");
    try {
      const confirmRes = await confirmAuthenticatorSetup(staffId, code);
      if (confirmRes.success !== 1) {
        setError(confirmRes.message || "Invalid code. Try again.");
        return;
      }

      const switchRes = await switchMfaLoginMethod(token, MFA_METHOD_AUTHENTICATOR);
      if (switchRes.success === 1 && switchRes.data) {
        onComplete({
          ...challenge,
          ...switchRes.data,
          NeedsAuthenticatorSetup: false,
          RequiresMethodSelection: false,
        });
      } else {
        setError(switchRes.message || "Setup confirmed but sign-in could not continue.");
      }
    } catch {
      setError("Could not confirm authenticator. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || loading;

  return (
    <div className="mfa-login-auth-setup">
      <div className="mfa-verification-header">
        <button type="button" className="mfa-back-btn" onClick={onBack} disabled={busy}>
          <i className="fas fa-arrow-left" aria-hidden="true" />
        </button>
        <h2 className="mfa-verification-title">Set up authenticator</h2>
      </div>

      <p className="mfa-login-auth-setup-desc">
        Scan the QR code with <strong>Google Authenticator</strong> or{" "}
        <strong>Microsoft Authenticator</strong>, then enter the 6-digit code to
        continue signing in.
      </p>

      {error && (
        <div className="login2-alert login2-alert-danger" role="alert">
          {error}
        </div>
      )}

      {otpAuthUri ? (
        <img
          className="mfa-login-auth-setup-qr"
          src={buildQrCodeUrl(otpAuthUri)}
          alt="Authenticator QR code"
          width={220}
          height={220}
        />
      ) : null}

      {secretKey ? (
        <p className="mfa-login-auth-setup-secret">
          Manual key: <code>{secretKey}</code>
        </p>
      ) : null}

      <div className="mfa-login-auth-setup-otp">
        {digits.map((digit, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            className="mfa-confirm-otp-input"
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            disabled={busy}
          />
        ))}
      </div>

      <button
        type="button"
        className="login2-btn-signin mfa-verify-btn"
        onClick={handleConfirm}
        disabled={busy || digits.some((d) => !d)}
      >
        {busy ? (
          <>
            <span className="login2-spinner" />
            Confirming…
          </>
        ) : (
          "Confirm & continue"
        )}
      </button>
    </div>
  );
}

export default MfaLoginAuthenticatorSetup;
